import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { auth, db } from '../../lib/supabase';
import api from '../../services/api';
import { preserveTrustedRailsProfile } from '../../utils/authRoles';
import { shouldHydrateProfileFromAuthEvent } from '../../utils/authSessionEvents';

// Timeout helper — resolves with fallback if promise takes too long
const withTimeout = (promise, ms, fallback) =>
  Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(fallback), ms)),
  ]);

const AUTH_TIMEOUT_MS = 5000; //5 seconds max for auth init
const PROFILE_TIMEOUT_MS = 4000;
const PROFILE_TIMEOUT = Symbol('profile-timeout');

const mergeProfiles = (supabaseProfile, backendProfile) => ({
  ...(supabaseProfile || {}),
  ...(backendProfile || {}),
  supabase_profile: supabaseProfile || null,
  rails_profile: backendProfile || null,
});

const applyAuthenticatedProfile = (state, { user, profile }) => {
  const sameUser = Boolean(user?.id && state.user?.id === user.id);

  state.profile = preserveTrustedRailsProfile(state.profile, profile, sameUser);
  state.user = user;
  state.isAuthenticated = !!user;
  state.loading = false;
};

const fetchSupabaseProfile = async (userId) => {
  try {
    const profileResult = await withTimeout(
      db.profiles.get(userId),
      3000,
      { data: null }
    );
    return profileResult.data;
  } catch (e) {
    return null;
  }
};

const fetchBackendProfile = async () => {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const profileResult = await withTimeout(
        api.get('/auth/profile'),
        PROFILE_TIMEOUT_MS,
        PROFILE_TIMEOUT
      );

      if (profileResult !== PROFILE_TIMEOUT) return profileResult.data;
    } catch (error) {
      const status = error?.response?.status;
      if (status && status < 500) return null;
    }
  }

  return null;
};

const fetchCombinedProfile = async (userId) => {
  const [supabaseProfile, backendProfile] = await Promise.all([
    fetchSupabaseProfile(userId),
    fetchBackendProfile(),
  ]);

  return mergeProfiles(supabaseProfile, backendProfile);
};

// Async thunks
export const initAuth = createAsyncThunk(
  'auth/init',
  async (_, { rejectWithValue }) => {
    try {
      // Wrap getSession in a timeout so the app never hangs if Supabase is down
      const result = await withTimeout(
        auth.getSession(),
        AUTH_TIMEOUT_MS,
        { session: null, error: null, timedOut: true }
      );

      if (result.timedOut) {
        console.warn('Supabase auth init timed out — treating as unauthenticated');
        return { user: null, profile: null };
      }

      const { session, error } = result;
      if (error) throw error;
      if (!session) return { user: null, profile: null };
      const profile = await fetchCombinedProfile(session.user.id);

      return {
        user: session.user,
        profile,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to initialize auth');
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const { data, error } = await auth.signIn({ email, password });
      if (error) throw error;
      const profile = await fetchCombinedProfile(data.user.id);

      return {
        user: data.user,
        profile,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

export const signup = createAsyncThunk(
  'auth/signup',
  async ({ email, password, firstName, lastName, phone, metadata }, { rejectWithValue }) => {
    try {
      const { data, error } = await auth.signUp({
        email,
        password,
        firstName,
        lastName,
        phone,
        metadata,
      });
      if (error) throw error;

      return {
        user: data.user,
        session: data.session,
        email, // Always pass the original email for verification
        message: data.session
          ? 'Account created successfully.'
          : 'Please check your email to confirm your account.',
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Signup failed');
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async (_, { rejectWithValue }) => {
  try {
    const { error } = await auth.signOut();
    if (error) throw error;
    return null;
  } catch (error) {
    return rejectWithValue(error.message || 'Logout failed');
  }
});

export const fetchProfile = createAsyncThunk(
  'auth/fetchProfile',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { user } = await auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const profile = await fetchCombinedProfile(user.id);

      return {
        user,
        profile,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch profile');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (updates, { getState, rejectWithValue }) => {
    try {
      const { user } = await auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data } = await api.patch('/auth/profile', { user: updates });
      const supabaseProfile = getState().auth.profile?.supabase_profile || null;
      const profile = mergeProfiles(supabaseProfile, data.user);

      return { profile };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update profile');
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (email, { rejectWithValue }) => {
    try {
      const { error } = await auth.resetPassword(email);
      if (error) throw error;
      return { message: 'Password reset email sent. Please check your inbox.' };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to send reset email');
    }
  }
);

export const signInWithGoogle = createAsyncThunk(
  'auth/signInWithGoogle',
  async (_, { rejectWithValue }) => {
    try {
      const { error } = await auth.signInWithGoogle();
      if (error) throw error;
      // OAuth redirects, so we don't return user data here
      return { message: 'Redirecting to Google...' };
    } catch (error) {
      return rejectWithValue(error.message || 'Google sign-in failed');
    }
  }
);

export const signInWithTwitter = createAsyncThunk(
  'auth/signInWithTwitter',
  async (_, { rejectWithValue }) => {
    try {
      const { error } = await auth.signInWithTwitter();
      if (error) throw error;
      return { message: 'Redirecting to Twitter...' };
    } catch (error) {
      return rejectWithValue(error.message || 'Twitter sign-in failed');
    }
  }
);

export const verifyEmail = createAsyncThunk(
  'auth/verifyEmail',
  async ({ email, token }, { rejectWithValue }) => {
    try {
      const { data, error } = await auth.verifyOTP(email, token);
      if (error) throw error;
      const profile = await fetchCombinedProfile(data.user.id);

      return {
        user: data.user,
        profile,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Email verification failed');
    }
  }
);

export const resendVerificationEmail = createAsyncThunk(
  'auth/resendVerificationEmail',
  async (email, { rejectWithValue }) => {
    try {
      const { error } = await auth.resendVerificationEmail(email);
      if (error) throw error;
      return { message: 'Verification code sent to your email' };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to resend verification email');
    }
  }
);

const initialState = {
  user: null,
  profile: null,
  isAuthenticated: false,
  loading: true, // Start with loading true for init
  error: null,
  message: null,
  needsVerification: false,
  verificationEmail: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    setUser: (state, action) => {
      applyAuthenticatedProfile(state, action.payload);
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Init Auth
      .addCase(initAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(initAuth.fulfilled, (state, action) => {
        state.loading = false;
        applyAuthenticatedProfile(state, action.payload);
        state.error = null;
      })
      .addCase(initAuth.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        state.profile = null;
        state.isAuthenticated = false;
        state.error = action.payload;
      })
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        applyAuthenticatedProfile(state, action.payload);
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      // Signup
      .addCase(signup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.message = action.payload.message;
        state.error = null;
        state.isAuthenticated = !!action.payload.session;
        state.needsVerification = !action.payload.session;
        state.verificationEmail = action.payload.session
          ? null
          : action.payload.email || action.payload.user?.email;
      })
      .addCase(signup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.profile = null;
        state.isAuthenticated = false;
        state.error = null;
        state.message = null;
      })
      // Fetch Profile
      .addCase(fetchProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.loading = false;
        applyAuthenticatedProfile(state, action.payload);
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
        state.profile = null;
      })
      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = preserveTrustedRailsProfile(
          state.profile,
          action.payload.profile,
          true
        );
        state.message = 'Profile updated successfully';
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Google Sign In
      .addCase(signInWithGoogle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signInWithGoogle.fulfilled, (state) => {
        state.loading = false;
        // OAuth redirects, state will be updated by auth listener
      })
      .addCase(signInWithGoogle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Twitter Sign In
      .addCase(signInWithTwitter.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signInWithTwitter.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(signInWithTwitter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Verify Email
      .addCase(verifyEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.loading = false;
        applyAuthenticatedProfile(state, action.payload);
        state.needsVerification = false;
        state.verificationEmail = null;
        state.error = null;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Resend Verification Email
      .addCase(resendVerificationEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendVerificationEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;
      })
      .addCase(resendVerificationEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearMessage, setUser, setLoading } = authSlice.actions;
export default authSlice.reducer;

// Auth state listener - call this in your App component
export const setupAuthListener = (dispatch) => {
  return auth.onAuthStateChange((event, session) => {
    const pathname = window.location.pathname;

    if (session && shouldHydrateProfileFromAuthEvent(event, pathname)) {
      // Supabase can deadlock when its APIs are awaited inside this callback.
      // Defer hydration so OAuth sessions can be read before fetching profiles.
      setTimeout(async () => {
        const profile = await fetchCombinedProfile(session.user.id);
        dispatch(setUser({ user: session.user, profile }));
      }, 0);
    } else if (event === 'SIGNED_OUT') {
      dispatch(setUser({ user: null, profile: null }));
    }
  });
};

